/**
 * AdminSettingsPage — Admin-configurable runtime settings (Calendly URL etc).
 *
 * Currently exposes:
 *   - Calendly intake URL (used in partner-welkomstmail + /platform/intake-plannen)
 *   - Calendly label
 *   - Calendly enabled toggle
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Save, Loader2, Calendar, ExternalLink } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminSettingsPage() {
  const { getToken } = useAuth();
  const token = getToken();
  const [settings, setSettings] = useState({
    calendly_url: '',
    calendly_label: '',
    calendly_enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/admin/app-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.settings) setSettings(res.data.settings);
      } catch (e) {
        toast.error('Kon instellingen niet laden');
      }
      setLoading(false);
    })();
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(
        `${BACKEND}/api/admin/app-settings`,
        {
          calendly_url: settings.calendly_url,
          calendly_label: settings.calendly_label,
          calendly_enabled: settings.calendly_enabled,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.settings) setSettings(res.data.settings);
      toast.success('Instellingen opgeslagen');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opslaan mislukt');
    }
    setSaving(false);
  };

  if (loading) {
    return <div style={{ padding: 24, color: '#94a3b8' }}><Loader2 size={16} className="animate-spin" /> Laden…</div>;
  }

  return (
    <div style={{ padding: 24, color: 'white', maxWidth: 760 }} data-testid="admin-settings-page">
      <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: 28, fontWeight: 800, margin: 0 }}>Algemene instellingen</h1>
      <p style={{ color: '#94a3b8', marginTop: 4, marginBottom: 24 }}>Runtime-instellingen die het hele platform gebruikt</p>

      {/* Calendly section */}
      <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 14, padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <div style={{ background: '#D4A017', width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Calendar size={18} color="#0f172a" />
          </div>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Calendly intake-link</h2>
            <p style={{ color: '#94a3b8', fontSize: 12, margin: '2px 0 0' }}>Gebruikt in welkomstmails en op /platform/intake-plannen voor goedgekeurde partners</p>
          </div>
        </div>

        <Label>Calendly URL</Label>
        <input
          type="url"
          value={settings.calendly_url || ''}
          onChange={(e) => setSettings({ ...settings, calendly_url: e.target.value })}
          data-testid="settings-calendly-url"
          placeholder="https://calendly.com/uw-account/intake"
          style={inputStyle}
        />
        <p style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Moet beginnen met https:// en calendly.com bevatten.</p>

        <Label style={{ marginTop: 16 }}>Label (zichtbaar voor partners)</Label>
        <input
          type="text"
          value={settings.calendly_label || ''}
          onChange={(e) => setSettings({ ...settings, calendly_label: e.target.value })}
          data-testid="settings-calendly-label"
          placeholder="IMPERIA AI — Intake"
          style={inputStyle}
        />

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, fontSize: 13, color: '#cbd5e1', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={!!settings.calendly_enabled}
            onChange={(e) => setSettings({ ...settings, calendly_enabled: e.target.checked })}
            data-testid="settings-calendly-enabled"
          />
          Calendly intake actief (uitschakelen toont melding &ldquo;intake tijdelijk niet beschikbaar&rdquo;)
        </label>

        {settings.calendly_url && (
          <a href={settings.calendly_url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#D4A017', fontSize: 13, marginTop: 14 }}>
            <ExternalLink size={12} /> Test deze link in nieuw tabblad
          </a>
        )}
      </div>

      <button onClick={save} disabled={saving} data-testid="settings-save" style={{ background: '#D4A017', color: '#0f172a', fontWeight: 700, padding: '12px 24px', borderRadius: 10, border: 'none', cursor: saving ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Opslaan
      </button>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  background: '#0f172a',
  color: 'white',
  border: '1px solid #1e293b',
  padding: '10px 12px',
  borderRadius: 8,
  fontSize: 14,
  marginTop: 6,
};

function Label({ children, style }) {
  return <label style={{ fontSize: 12, fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.06em', ...style }}>{children}</label>;
}
